// @ts-nocheck
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import { ReactComponent as AddIcon } from '../../assets/Icons/lucideIcons/plus.svg';

import ResizeHandler from '../TableSchemaBuilder/components/ResizeHandler';

const ConfigTableRenderer = ({
  columns,
  rows,
  isViewMode,
  handleAddRowClick,
  openMenu,
  isMergedRow,
  getDisplayValue,
  handleResizeColumn,
}) => {
  const theme = useTheme();

  // Pre-bind resize handlers per column to avoid inline functions in JSX
  const resizeHandlers = useMemo(() => {
    if (!handleResizeColumn) return {};
    const handlers = {};
    columns.forEach((col) => {
      if (col?.colName) {
        handlers[col.colName] = (newWidth) =>
          handleResizeColumn(col.colName, newWidth);
      }
    });
    return handlers;
  }, [columns, handleResizeColumn]);

  const handleContextMenu = useCallback(
    (e) => {
      e?.preventDefault?.();
      const rowIdx = Number.parseInt(e.currentTarget.dataset.rowindex, 10);
      if (!isViewMode && openMenu) {
        openMenu(e, rowIdx);
      }
    },
    [isViewMode, openMenu]
  );

  const defaultMesssage = useMemo(() => {
    if (isViewMode) {
      return 'No entries available. Switch to edit mode to add rows.';
    }
    return 'No default row entries yet. Click "+ Add Row" to start.';
  }, [isViewMode]);

  const headerCellSx = {
    backgroundColor: alpha(theme?.palette?.primary?.main, 0.05),
    borderRight: `1px solid ${alpha(theme?.palette?.primary?.main, 0.2)}`,
    borderBottom: `1px solid ${alpha(theme?.palette?.primary?.main, 0.2)}`,
    color: theme?.palette?.primary?.main,
    fontWeight: 700,
    fontSize: '12px',
    textTransform: 'uppercase',
    py: 1.25,
    px: 1.5,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    userSelect: 'none',
  };

  const dataCellSx = {
    border: `1px solid ${alpha(theme?.palette?.primary?.main, 0.08)}`,
    backgroundColor:
      theme?.palette?.other?.cell || theme?.palette?.background?.paper,
    py: 1.5,
    px: 1.5,
    fontSize: '14px',
    color: theme?.palette?.text?.primary,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  };

  const mergedCellSx = {
    border: `1px solid ${alpha(theme?.palette?.primary?.main, 0.08)}`,
    backgroundColor: alpha(theme?.palette?.primary?.main, 0.03),
    py: 1.5,
    px: 1.5,
    fontSize: '13px',
    fontWeight: 600,
    color: theme?.palette?.text?.primary,
  };

  return (
    <Paper
      elevation={1}
      sx={{
        flex: '0 1 auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 0,
        '& .MuiPaper-root': {
          paddingBottom: '0px',
        },
      }}
    >
      <TableContainer
        sx={{
          flex: '0 1 auto',
          padding: 0,
          overflowY: 'auto',
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      >
        <Table
          sx={{
            tableLayout: 'fixed',
            minWidth: '100%',
            '& .MuiFormControl-root.MuiTextField-root': { marginTop: 0 },
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              border: 'none',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            },
          }}
        >
          <TableBody>
            {/* ── Header row ── */}
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col?.label}
                  sx={{
                    ...headerCellSx,
                    width: col?.width || 150,
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: theme?.palette?.text?.secondary,
                      userSelect: 'none',
                      position: 'relative',
                    }}
                  >
                    <Typography
                      variant="body4"
                      title={`${col.label}${col.id ? ` (${col.id})` : ''}`}
                      sx={{
                        color: 'primary.main',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        maxWidth: '100%',
                        px: 1,
                        lineHeight: 1.5,
                      }}
                    >
                      {col.label}
                    </Typography>
                  </div>
                  {handleResizeColumn && (
                    <ResizeHandler
                      orientation="horizontal"
                      onResize={resizeHandlers[col?.colName]}
                      initialSize={col.width || 150}
                      minSize={50}
                      ariaLabel={`Resize column ${col.label}`}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>

            {/* ── Data rows ── */}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{
                    verticalAlign: 'middle',
                    color: 'text.disabled',
                    fontStyle: 'italic',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme?.palette?.primary?.main, 0.1)}`,
                    backgroundColor:
                      theme?.palette?.other?.cell ||
                      theme?.palette?.background?.paper,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                      color: 'text.disabled',
                    }}
                  >
                    <Typography variant="body6">{defaultMesssage}</Typography>
                    {!isViewMode && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddRowClick}
                      >
                        Add First Row
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIdx) => {
                const merged = isMergedRow(rowIdx);
                const firstColName = columns[0]?.colName || 'A';
                const mergedText = merged ? (row[firstColName] ?? '') : '';

                return (
                  <TableRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`row-${rowIdx}`}
                    data-rowindex={rowIdx}
                    onContextMenu={handleContextMenu}
                    sx={{
                      cursor: isViewMode ? 'default' : 'context-menu',
                      '&:hover td': {
                        backgroundColor: alpha(
                          theme?.palette?.primary?.main,
                          0.03
                        ),
                      },
                    }}
                  >
                    {merged ? (
                      /* ── Merged (custom) row — single cell spanning all columns ── */
                      <TableCell colSpan={columns.length} sx={mergedCellSx}>
                        <Box
                          sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 8px',
                            minHeight: '44px',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: theme?.palette?.text?.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getDisplayValue(mergedText, null)}
                        </Box>
                      </TableCell>
                    ) : (
                      /* ── Default row — individual cells ── */
                      columns.map((col, colIdx) => {
                        const colName =
                          col.colName || String.fromCharCode(65 + colIdx); // Fallback to 'A', 'B' etc
                        const cellVal = row[colName];

                        return (
                          <TableCell
                            key={colName}
                            sx={{ ...dataCellSx, width: col.width || 150 }}
                          >
                            <Box
                              sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px 6px',
                                minHeight: '44px',
                              }}
                            >
                              <Typography
                                title={getDisplayValue(cellVal, col)}
                                sx={{
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  whiteSpace: 'pre-line',
                                  width: '100%',
                                  lineHeight: 1.5,
                                }}
                              >
                                {getDisplayValue(cellVal, col)}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      })
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

ConfigTableRenderer.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      colName: PropTypes.string,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isViewMode: PropTypes.bool,
  handleAddRowClick: PropTypes.func,
  openMenu: PropTypes.func,
  isMergedRow: PropTypes.func.isRequired,
  getDisplayValue: PropTypes.func.isRequired,
  handleResizeColumn: PropTypes.func,
};

export default ConfigTableRenderer;
